/** MapLibre GL map: raster basemaps online, the PMTiles pack when a project has one, zones and condition-coloured trees. */
import { CONDITION_COLORS, UNMEASURED_COLOR, type FeatureCollection, type PointGeometry, type PolygonGeometry, type TreeFeatureProps } from '@treetrack/protocol';
import { addProtocol, GeoJSONSource, GeolocateControl, Map as MlMap, NavigationControl, ScaleControl, setWorkerUrl, type LayerSpecification, type MapLayerMouseEvent, type StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
// maplibre 6 is ESM-only and finds its worker with new URL('./maplibre-gl-worker.mjs', import.meta.url),
// which Vite does not emit: the built site 404s the worker and every layer stays blank (bluewater-iq,
// 14 Sep 2026). Bundle the worker as a Vite worker asset and hand maplibre that URL up front.
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import { Protocol } from 'pmtiles';
import themeLayers from 'protomaps-themes-base';

export type BasemapKind = 'satellite' | 'streets' | 'pack';

/** San Juan del Sur, Nicaragua: the default view when a project has no trees yet. */
export const SJDS_CENTER: [number, number] = [-85.87, 11.25];

setWorkerUrl(maplibreWorkerUrl);

let protocolRegistered = false;
function ensureProtocol() {
  if (protocolRegistered) return;
  const protocol = new Protocol();
  addProtocol('pmtiles', protocol.tile);
  protocolRegistered = true;
}

const ESRI = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const OSM = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

export function basemapStyle(kind: BasemapKind, packUrl?: string | null): StyleSpecification {
  if (kind === 'pack' && packUrl) {
    ensureProtocol();
    return {
      version: 8,
      glyphs: 'https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf',
      sprite: 'https://protomaps.github.io/basemaps-assets/sprites/v4/light',
      sources: { basemap: { type: 'vector', url: `pmtiles://${packUrl}`, attribution: '© OpenStreetMap contributors · Protomaps' } },
      layers: themeLayers('basemap', 'light', 'es') as LayerSpecification[],
    };
  }
  const satellite = kind === 'satellite';
  return {
    version: 8,
    sources: {
      raster: {
        type: 'raster',
        tiles: [satellite ? ESRI : OSM],
        tileSize: 256,
        maxzoom: satellite ? 19 : 19,
        attribution: satellite ? 'Esri, Maxar, Earthstar Geographics, and the GIS User Community' : '© OpenStreetMap contributors',
      },
    },
    layers: [{ id: 'raster', type: 'raster', source: 'raster' }],
  };
}

export interface MapHandles {
  map: MlMap;
  setBasemap(kind: BasemapKind, packUrl?: string | null): void;
  setData(zones: FeatureCollection<{ zone_id: string; label: string }, PolygonGeometry>, trees: FeatureCollection<TreeFeatureProps, PointGeometry>): void;
  fitTo(box: [number, number, number, number] | null): void;
  onTreeClick(fn: (treeId: string) => void): void;
  destroy(): void;
}

const EMPTY: FeatureCollection<never, never> = { type: 'FeatureCollection', features: [] };

export function createMap(container: HTMLElement, kind: BasemapKind = 'satellite', packUrl?: string | null): MapHandles {
  const map = new MlMap({
    container,
    style: basemapStyle(kind, packUrl),
    center: SJDS_CENTER,
    zoom: 13,
    attributionControl: { compact: true },
  });
  map.addControl(new NavigationControl({ showCompass: false }), 'top-right');
  map.addControl(new GeolocateControl({ positionOptions: { enableHighAccuracy: true }, trackUserLocation: true }), 'top-right');
  map.addControl(new ScaleControl({ unit: 'metric' }), 'bottom-left');

  let zones: unknown = EMPTY;
  let trees: unknown = EMPTY;
  let clickFn: ((treeId: string) => void) | null = null;

  const colorExpr = [
    'match',
    ['get', 'condition'],
    'H',
    CONDITION_COLORS.H.fill,
    'S',
    CONDITION_COLORS.S.fill,
    'D',
    CONDITION_COLORS.D.fill,
    'NF',
    CONDITION_COLORS.NF.fill,
    UNMEASURED_COLOR.fill,
  ];
  const strokeExpr = [
    'match',
    ['get', 'condition'],
    'H',
    CONDITION_COLORS.H.stroke,
    'S',
    CONDITION_COLORS.S.stroke,
    'D',
    CONDITION_COLORS.D.stroke,
    'NF',
    CONDITION_COLORS.NF.stroke,
    UNMEASURED_COLOR.stroke,
  ];

  function addOverlays() {
    if (map.getSource('zones')) return;
    map.addSource('zones', { type: 'geojson', data: zones as never });
    map.addSource('trees', { type: 'geojson', data: trees as never });
    map.addLayer({ id: 'zones-fill', type: 'fill', source: 'zones', paint: { 'fill-color': '#66bb6a', 'fill-opacity': 0.08 } });
    map.addLayer({ id: 'zones-line', type: 'line', source: 'zones', paint: { 'line-color': '#fbfaf5', 'line-width': 2, 'line-dasharray': [2, 1] } });
    map.addLayer({
      id: 'zones-label',
      type: 'symbol',
      source: 'zones',
      layout: { 'text-field': ['get', 'label'], 'text-size': 14, 'text-font': ['Noto Sans Regular'] },
      paint: { 'text-color': '#fbfaf5', 'text-halo-color': '#1b5e20', 'text-halo-width': 1.5 },
    });
    map.addLayer({
      id: 'trees-circle',
      type: 'circle',
      source: 'trees',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 13, 3, 17, 7, 20, 10],
        'circle-color': colorExpr as never,
        'circle-stroke-color': strokeExpr as never,
        'circle-stroke-width': 2,
        'circle-opacity': 0.95,
      },
    });
    map.addLayer({
      id: 'trees-label',
      type: 'symbol',
      source: 'trees',
      minzoom: 18,
      layout: { 'text-field': ['get', 'tag'], 'text-size': 11, 'text-offset': [0, 1.1], 'text-anchor': 'top', 'text-font': ['Noto Sans Regular'] },
      paint: { 'text-color': '#1f2a1f', 'text-halo-color': '#fbfaf5', 'text-halo-width': 1.2 },
    });
    map.on('click', 'trees-circle', (e: MapLayerMouseEvent) => {
      const f = e.features?.[0];
      const id = (f?.properties as { tree_id?: string } | undefined)?.tree_id;
      if (id && clickFn) clickFn(id);
    });
    map.on('mouseenter', 'trees-circle', () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', 'trees-circle', () => {
      map.getCanvas().style.cursor = '';
    });
  }

  map.on('load', addOverlays);
  // Raster styles have no glyphs; symbol layers then need a glyph source. Provide one.
  map.on('styledata', () => {
    if (!map.getStyle().glyphs) {
      try {
        map.setGlyphs('https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf');
      } catch {
        /* style not ready */
      }
    }
  });

  return {
    map,
    setBasemap(k, url) {
      map.setStyle(basemapStyle(k, url));
      map.once('styledata', () => {
        if (!map.getStyle().glyphs) map.setGlyphs('https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf');
        addOverlays();
      });
    },
    setData(z, tr) {
      zones = z;
      trees = tr;
      const zs = map.getSource('zones') as GeoJSONSource | undefined;
      const ts = map.getSource('trees') as GeoJSONSource | undefined;
      if (zs) zs.setData(z as never);
      if (ts) ts.setData(tr as never);
    },
    fitTo(box) {
      if (!box) return;
      const [a, b, c, d] = box;
      if (a === c && b === d) map.jumpTo({ center: [a, b], zoom: 17 });
      else map.fitBounds([a, b, c, d], { padding: 40, maxZoom: 18, duration: 0 });
    },
    onTreeClick(fn) {
      clickFn = fn;
    },
    destroy() {
      map.remove();
    },
  };
}

/** Legend swatches for the map and the roster. */
export function legendEntries(): Array<{ key: 'H' | 'S' | 'D' | 'NF' | 'unmeasured'; fill: string; stroke: string }> {
  return [
    { key: 'H', ...CONDITION_COLORS.H },
    { key: 'S', ...CONDITION_COLORS.S },
    { key: 'D', ...CONDITION_COLORS.D },
    { key: 'NF', ...CONDITION_COLORS.NF },
    { key: 'unmeasured', ...UNMEASURED_COLOR },
  ];
}
